import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Agent {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'error';
  lastRun: string;
}

interface AgentTemplateEntry {
  name: string;
  role: string;
  goal: string;
  triggers: string[];
}

const AGENT_LABELS: Record<string, string> = {
  research: 'Research',
  analyst: 'Analytics',
  writer: 'Content',
  monitor: 'Monitoring',
  coordinator: 'Coordination',
  qa: 'Quality Assurance',
};

const MOCK_AGENTS: Agent[] = [
  { id: '1', name: 'Research Bot', type: 'Research', status: 'active', lastRun: '2 min ago' },
  { id: '2', name: 'Code Reviewer', type: 'Development', status: 'active', lastRun: '15 min ago' },
  { id: '3', name: 'Data Analyst', type: 'Analytics', status: 'inactive', lastRun: '1 hour ago' },
];

function buildAgentList(data: { active_agents?: string[]; active_tasks?: number; completed_tasks?: number }): Agent[] {
  if (!data.active_agents || !Array.isArray(data.active_agents) || data.active_agents.length === 0) {
    return MOCK_AGENTS;
  }
  return data.active_agents.map((type, idx) => ({
    id: `api-${type}-${idx}`,
    name: AGENT_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1),
    type: AGENT_LABELS[type] || type,
    status: 'active' as const,
    lastRun: data.active_tasks && data.active_tasks > 0 ? `${data.active_tasks} task(s) running` : 'Idle',
  }));
}

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('Research');
  const [formDescription, setFormDescription] = useState('');
  const [successId, setSuccessId] = useState<string | null>(null);
  const [, setTemplates] = useState<Record<string, AgentTemplateEntry>>({});

  useEffect(() => {
    let cancelled = false;
    const fetchAgents = async () => {
      try {
        const response = await api.get('/api/v1/agents');
        if (!cancelled) {
          const data = response.data;
          if (Array.isArray(data)) {
            setAgents(data);
          } else if (data.agents && Array.isArray(data.agents)) {
            setAgents(data.agents);
          } else {
            setAgents(buildAgentList(data));
          }
        }
      } catch {
        if (!cancelled) {
          setAgents(MOCK_AGENTS);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAgents();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const response = await api.get('/api/v1/agents/templates');
        if (!cancelled && response.data?.templates) {
          setTemplates(response.data.templates);
        }
      } catch {
        // Templates are optional; silently ignore failures
      }
    };

    fetchTemplates();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal]);

  const resetForm = () => {
    setFormName('');
    setFormType('Research');
    setFormDescription('');
    setCreating(false);
  };

  const handleCreate = async () => {
    if (!formName.trim()) return;
    setCreating(true);
    // Simulate brief async delay
    await new Promise((r) => setTimeout(r, 600));
    const newAgent: Agent = {
      id: Date.now().toString(),
      name: formName.trim(),
      type: formType,
      status: 'active',
      lastRun: 'Just now',
    };
    setAgents((prev) => [...prev, newAgent]);
    setSuccessId(newAgent.id);
    resetForm();
    setShowModal(false);
    setTimeout(() => setSuccessId(null), 2000);
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent?')) return;
    try {
      await api.delete(`/api/v1/agents/${agentId}`);
    } catch {
      // local removal only
    }
    setAgents(prev => prev.filter(a => a.id !== agentId));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Agents</h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          + New Agent
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((agent) => (
          <div
            key={agent.id}
            className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border hover:shadow-md transition-shadow ${
              successId === agent.id
                ? 'border-green-400 dark:border-green-500 ring-2 ring-green-200 dark:ring-green-800'
                : 'border-gray-100 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">{agent.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{agent.type}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(agent.status)}`}>
                {agent.status}
              </span>
              <button
                onClick={() => handleDeleteAgent(agent.id)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete agent"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
              Last run: {agent.lastRun}
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-modal-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 id="agent-modal-title" className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Create New Agent
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Research Bot"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent Type
                </label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  <option>Research</option>
                  <option>Development</option>
                  <option>Analytics</option>
                  <option>Support</option>
                  <option>Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="What does this agent do?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!formName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                Create Agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
